import { useMemo } from 'react';
import type { ArcProperties, VoiceSpec } from '../zerovoice/types';
import { KOKORO_VOICES } from '../zerovoice/voiceMapping';

type PropName = 'curveWarp' | 'harmonicWeight' | 'spectralSkew' | 'kinship';

interface GridProps {
  table: ArcProperties[][];
  prop: PropName;
  onCellClick: (a: number, b: number) => void;
}

function colorFor(prop: PropName, value: number): string {
  // Simple lightness ramp per-property. Values outside their nominal range
  // are clamped [0, 1] for display.
  let norm: number;
  switch (prop) {
    case 'curveWarp':      norm = (value - 0.2) / 0.6; break;   // [0.2, 0.8] → [0,1]
    case 'harmonicWeight': norm = value / 0.3; break;            // [0, 0.3]
    case 'spectralSkew':   norm = (value + 0.15) / 0.30; break;  // [-0.15, +0.15]
    case 'kinship':        norm = value; break;                  // [0, 1]
  }
  norm = Math.max(0, Math.min(1, norm));
  const hues: Record<PropName, number> = {
    curveWarp: 210,       // blue → amber
    harmonicWeight: 280,  // grey → purple
    spectralSkew: 10,     // red → green via hue shift below
    kinship: 0,           // greyscale
  };
  const hue = hues[prop];
  if (prop === 'kinship') {
    const grey = Math.round(norm * 255);
    return `rgb(${grey}, ${grey}, ${grey})`;
  }
  if (prop === 'spectralSkew') {
    // Red (value<0.5) → green (value>0.5) via hue shift
    const h = norm < 0.5 ? 0 : 120;
    const l = 30 + Math.abs(norm - 0.5) * 60; // 30% to 60%
    return `hsl(${h}, 70%, ${l}%)`;
  }
  const l = 25 + norm * 55;
  return `hsl(${hue}, 70%, ${l}%)`;
}

function ArcGrid({ table, prop, onCellClick }: GridProps) {
  const N = table.length;
  return (
    <div
      role="grid"
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${N}, 14px)`,
        gap: 1,
      }}
    >
      {table.map((row, a) =>
        row.map((cell, b) => (
          <button
            key={`${a}:${b}`}
            type="button"
            onClick={() => onCellClick(a, b)}
            title={`${KOKORO_VOICES[a]} × ${KOKORO_VOICES[b]} ${prop}=${cell[prop].toFixed(3)}`}
            style={{
              width: 14,
              height: 14,
              backgroundColor: colorFor(prop, cell[prop]),
              border: 'none',
              padding: 0,
              cursor: 'pointer',
            }}
            aria-label={`${KOKORO_VOICES[a]} to ${KOKORO_VOICES[b]}`}
          />
        )),
      )}
    </div>
  );
}

interface Props {
  arcTable: ArcProperties[][] | null;
  onPlayBlend: (spec: VoiceSpec) => void;
}

export function ZeroVoiceArcInspector({ arcTable, onPlayBlend }: Props) {
  const grids = useMemo<Array<{ prop: PropName; title: string; range: string }>>(
    () => [
      { prop: 'curveWarp',      title: 'Curve warp',      range: '[0.2, 0.8]' },
      { prop: 'harmonicWeight', title: 'Harmonic weight', range: '[0.0, 0.3]' },
      { prop: 'spectralSkew',   title: 'Spectral skew',   range: '[-0.15, +0.15]' },
      { prop: 'kinship',        title: 'Kinship (cos-sim)', range: '[0.0, 1.0]' },
    ],
    [],
  );

  if (!arcTable) {
    return (
      <div className="muted small">
        Voice bundle not loaded — arc table unavailable.
      </div>
    );
  }

  const handleCellClick = (a: number, b: number): void => {
    onPlayBlend({ voiceA: a, voiceB: b, t: 0.5, pitch: 1.0, energy: 1.0 });
  };

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))',
        gap: '1em',
      }}
    >
      {grids.map((g) => (
        <div key={g.prop}>
          <div className="small" style={{ marginBottom: 4 }}>
            <strong>{g.title}</strong>{' '}
            <span className="muted">{g.range}</span>
          </div>
          <ArcGrid
            table={arcTable}
            prop={g.prop}
            onCellClick={handleCellClick}
          />
        </div>
      ))}
      <div className="muted small" style={{ gridColumn: '1 / -1' }}>
        Click any cell to play a 1-second sample of the blend at t=0.5,
        pitch=1.0, energy=1.0.
      </div>
    </div>
  );
}
